import * as React from 'react'
import { Redirect } from 'react-router-dom'
import styled from 'styled-components'
import { compose, layout, LayoutProps } from 'styled-system'
import { FormikHelpers } from 'formik'

import { Box, Button, TextBody, upToMd } from '@ally/metronome-ui'
import { Form, Forms, SSN } from '@ally/forms'
import { ConfirmSsnProps } from '@ally/transmitigator'
import { FormField, LoginHeading } from '@ally/ally-login'

import { SSNModal } from '../../Modals'
import { useTransmit } from '../../TransmitContext'
import { CIAMErrorBanner } from '../Shared'
import { useAppState } from '../../AppStateProvider'

const StyledForm = styled(Form)<LayoutProps>`
  ${compose(layout)};
`

const FORM_ID = 'ally-login-forgot-username-confirm-ssn'

interface ConfirmSSNForm {
  ssn: string
}

const initialValues: ConfirmSSNForm = {
  ssn: '',
}

const ConfirmSSNContent: React.FC<ConfirmSsnProps> = ({
  error,
  status,
  errorCode,
  onComplete,
}) => {
  const { hostServices } = useTransmit()

  const onSubmit = (
    form: ConfirmSSNForm,
    { setSubmitting }: FormikHelpers<ConfirmSSNForm>,
  ): void => {
    onComplete({
      ssn: form.ssn,
    })
    // NOTE: this allows for form resubmission after failure
    setSubmitting(false)
  }

  const { isWebWrapped } = useAppState()

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: isWebWrapped
        ? 'ally mobile app:Web View:Forgot Username:Enter Social Security Number'
        : 'AOS:Login:Forgot Username:Recovery Option',
    })
  }, [])

  if (error && error === 'CIAM_E08_00_004') {
    return <Redirect to="/security/error" />
  }

  return (
    <>
      <LoginHeading title="Forgot Username" />
      <Box mb="md" display="flex" width={upToMd<any, any>(12, 6) as any}>
        <TextBody tag="p" size="md">
          To verify that it&apos;s you, enter your full Social Security number.
        </TextBody>
      </Box>

      <CIAMErrorBanner error={errorCode} mb="md" />

      <Box display="flex">
        <Forms
          formId={FORM_ID}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          <StyledForm id={FORM_ID} autoComplete="off" width={12}>
            <FormField>
              <Box mb="md" width={upToMd(12, 7)}>
                <SSN
                  label="Social security number"
                  name="ssn"
                  data-allytmfn="ssn"
                  required
                  data-testid="forgot-username-ssn-input"
                />
              </Box>
              <Box mb="md">
                <SSNModal
                  textBodyType="forgotPassword"
                  withAllyTM={
                    isWebWrapped
                      ? 'ally mobile app:Web View:Forgot Username:Enter Social Security Number|Why We Need SSN'
                      : undefined
                  }
                />
              </Box>

              <Button
                allytmln="continue"
                type="submit"
                variant="primary"
                text="Continue"
                loading={status === 'Loading'}
              />
            </FormField>
          </StyledForm>
        </Forms>
      </Box>
    </>
  )
}

export const ConfirmSSN: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'ConfirmSSN') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <ConfirmSSNContent {...activeScreen.payload} />
}
